import { getQueryKey, useQuery, useSuspenseQuery } from '~/services'
import { DataGrid, DefaultDataGridCells, Button, Column, RenderProps, FileCell } from '@bpinternal/ui-kit-next'
import { ClientInputs, ClientOutputs, type File, type Row } from '@botpress/client'
import { match, P } from 'ts-pattern'
import { Page } from '~/components'
import { ChevronDown, ChevronLeft, ChevronRight, FilterIcon, RefreshCw, Rows, X } from 'lucide-react'
import { Popover, Icon, Input } from '~/elements'
import { FC, memo, useCallback, useMemo, useRef, useState, type ComponentProps } from 'react'
import { useDebounce } from 'react-use'
import clsx from 'clsx'
import { toLowerCase, toUpperCase } from '~/utils'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { userPreferencesQueryOptions } from '~/queries'
import { useSetAccountPreferences, useUploadFile } from '~/hooks'
import { useDebounceCallback } from 'usehooks-ts'
import { FilterQueryBuilder, QBResult, QBSchema } from '~/features/tables/components/FilterQueryBuilder'
import { useUpsertTableRows } from '~/hooks/tables'
import { queryClient } from '~/providers/ReactQuery'

const DEFAULT_LIMIT = 50
const DEFAULT_OFFSET = 0

export type TableProps = {
  params: {
    tableId: string
    workspaceId: string
    botId: string
  }
  searchParams: {
    limit: number
    offset: number
    filter?: Record<string, any> | undefined
    group?: Record<string, any> | undefined
    orderBy?: string | undefined
    orderDirection?: 'asc' | 'desc' | undefined
    search?: string | undefined
  }
  onSearchParamsUpdated: (searchParams: TableProps['searchParams']) => void
}

export const Table: FC<TableProps> = ({ params, searchParams, onSearchParamsUpdated }) => {
  const { data: table } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_/tables/$table_', {
    ...params,
    table: params.tableId,
  })

  const { data: columSizes } = useTanstackSuspenseQuery(
    userPreferencesQueryOptions({ path: '$tableId/columnWidths', params })
  )
  const { mutateAsync: uploadFileAsync } = useUploadFile({ workspaceId: params.workspaceId, botId: params.botId })

  const { mutate: setPreferences } = useSetAccountPreferences()

  const {
    data: tableRows,
    isFetching: isFetchingRows,
    isFetched: isFetchedRows,
    refetch: refetchRows,
  } = useQuery(
    'workspaces_/$workspaceId_/bots_/$botId_/tables/$table_/rows/$filter/$limit/$group/$offset/$orderBy/$orderDirection/$search',
    { ...params, table: params.tableId, ...searchParams }
  )

  const currentPage = (searchParams.offset ?? DEFAULT_OFFSET) / (searchParams.limit ?? DEFAULT_LIMIT) + 1
  const totalPages = Math.ceil(table.rows / (searchParams.limit ?? DEFAULT_LIMIT))

  const parsedColumns = parseColumns(table.table, columSizes)
  const filesColumnKeys = parsedColumns.filter(({ cell }) => cell.type === 'file').map(({ key }) => key)
  const fileIds = tableRows?.rows.flatMap((row) => filesColumnKeys.map((key) => row[key])).filter(Boolean)

  const { data: files } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/files/$ids/$tags/all', {
    ...params,
    ids: fileIds,
  })

  const { mutate: upsertTableRows } = useUpsertTableRows({
    ...params,
  })

  const parsedRows = parseRows({
    parsedColumns,
    tableRows: tableRows?.rows ?? [],
    files: files ?? [],
  })

  const skeletonRows = useMemo(() => generateRows({ columns: parsedColumns }), [parsedColumns])

  function jumpToPage(page: number) {
    const nextPage = match(page)
      .with(P.number.lt(1), () => 1)
      .with(P.number.gt(totalPages), () => totalPages)
      .with(P.number, () => page)
      .otherwise(() => 1)

    onSearchParamsUpdated({
      ...searchParams,
      offset: Math.max(nextPage - 1, 0) * (searchParams.limit ?? DEFAULT_LIMIT),
    })
  }
  const handleFileUpload = useCallback<RenderProps<FileCell>['uploadFile']>(
    async (file) => {
      const uploadedFile = await uploadFileAsync({
        key: file.name,
        content: file,
        contentType: file.type,
        accessPolicies: ['public_content'],
        tags: { source: 'table', table: table.table.id },
      })
      if (!uploadedFile) {
        return null
      }
      queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/files/$ids/$tags/all', {
          ...params,
          ids: [uploadedFile?.file.id],
          tags: { source: 'table', table: table.table.id },
        }),
      })
      return {
        id: uploadedFile?.file.id,
        url: uploadedFile?.file.url,
        key: uploadedFile?.file.key,
        contentType: uploadedFile?.file.contentType,
      }
    },
    [uploadFileAsync, table.table.id, params]
  )
  const [desiredPage, setDesiredPage] = useState(currentPage)
  useDebounce(
    () => {
      if (inputRef.current === document.activeElement) {
        return
      }
      jumpToPage(desiredPage)
    },
    250,
    [desiredPage]
  )

  const updateColumnWidth = useDebounceCallback((key: string, width: number) => {
    setPreferences({ path: '$tableId/columnWidths', params, value: { ...columSizes, [key]: width } })
  }, 1000)

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Page title={table.table.name}>
      <div className="flex gap-4 pb-2 pl-1">
        <ConfigureTableFilter
          schema={table.table.schema}
          initialTableFilter={searchParams.filter}
          onTableFilterUpdated={(newFilter: QBSchema | undefined) => {
            onSearchParamsUpdated({ ...searchParams, filter: newFilter })
          }}
        />
        <Button variant="ghost" onClick={() => refetchRows()} disabled={isFetchingRows} color="gray" size="sm">
          <RefreshCw />
          Refresh
        </Button>
      </div>
      <div className="flex flex-col items-center gap-4">
        {tableRows?.rows.length === 0 ? (
          <div className="flex h-full max-h-[calc(100dvh-450px)] min-h-[500px] w-full flex-col items-center justify-center gap-4 rounded-lg border">
            <Icon icon={Rows} className="h-10 w-10" />
            <span className="text-muted-foreground">No rows found</span>
          </div>
        ) : (
          <MDataGrid
            editable={!table.table.frozen}
            enableVirtualization={false}
            className={clsx('h-full max-h-[calc(100dvh-450px)] min-h-[500px] w-full rounded-lg')}
            renderers={{}}
            columns={parsedColumns}
            onRowsChange={(rows, changes) => {
              upsertTableRows({
                rows: serializeRows(rows as Record<string, DefaultDataGridCells>[], changes.indexes),
                table: table.table.id,
                keyColumn: 'id',
              })
            }}
            onFileUpload={handleFileUpload}
            defaultColumnOptions={{ sortable: true }}
            sortColumns={
              searchParams.orderBy && searchParams.orderDirection
                ? [{ columnKey: searchParams.orderBy, direction: toUpperCase(searchParams.orderDirection) }]
                : []
            }
            onSortColumnsChange={(cols) => {
              const sortColumn = cols[0]

              if (!sortColumn) {
                onSearchParamsUpdated({ ...searchParams, orderBy: undefined, orderDirection: undefined })
                return
              }

              const { columnKey: orderBy, direction } = sortColumn
              onSearchParamsUpdated({ ...searchParams, orderBy, orderDirection: toLowerCase(direction) })
            }}
            rows={isFetchedRows ? parsedRows : skeletonRows}
            onColumnResize={(idx, width) => {
              const key = parsedColumns[idx]?.key
              if (key) {
                updateColumnWidth(key, width)
              }
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setDesiredPage((prev) => prev - 1)}
            disabled={desiredPage === 1}
            color="gray"
            size="sm"
            variant="ghost"
          >
            <ChevronLeft />
          </Button>
          <div className="flex items-baseline gap-2">
            <Input
              ref={inputRef}
              onBlur={(e) => {
                jumpToPage(parseInt(e.target.value))
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  jumpToPage(parseInt(e.currentTarget.value))
                  inputRef.current?.blur()
                }
              }}
              value={desiredPage}
              onChange={(e) => setDesiredPage(parseInt(e.target.value))}
              type="number"
              className="w-8"
            />
            <span className="text-muted-foreground">of {Math.max(totalPages, 1)}</span>
          </div>
          <Button
            onClick={() => setDesiredPage((prev) => prev + 1)}
            disabled={desiredPage >= totalPages}
            color="gray"
            variant="ghost"
            size="sm"
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </Page>
  )
}

type ConfigureTableFilterProps = {
  schema: ClientOutputs['getTable']['table']['schema']
  initialTableFilter: QBSchema | undefined
  onTableFilterUpdated: (props: QBSchema | undefined) => void
}

const DEFAULT_QUERY_CONFIG: QBResult = Object.freeze({ query: null, rulesCount: 0, isValid: false })

const getLabelFilter = (filters: number) =>
  filters > 0 ? `Filtered by ${filters} rule${filters > 1 ? 's' : ''}` : 'Filter'

const ConfigureTableFilter: FC<ConfigureTableFilterProps> = ({ schema, initialTableFilter, onTableFilterUpdated }) => {
  const initialQueryConfig: QBResult = initialTableFilter
    ? { query: JSON.stringify(initialTableFilter), rulesCount: Object.values(initialTableFilter).length, isValid: true }
    : DEFAULT_QUERY_CONFIG

  const [isOpened, setIsOpened] = useState(false)
  const [activeRules, setActiveRules] = useState<number>(initialQueryConfig.rulesCount)

  const clearAllFilters = () => {
    setActiveRules(0)
    onTableFilterUpdated({})
  }

  return (
    <Popover
      open={isOpened}
      onOpenChange={setIsOpened}
      trigger={
        <div className="flex items-center">
          <Button
            className={activeRules > 0 ? 'rounded-none rounded-l-md' : 'rounded'}
            variant="ghost"
            size="sm"
            color={activeRules > 0 ? 'grass' : 'blue'}
          >
            <FilterIcon />
            {getLabelFilter(activeRules)}
          </Button>
          {activeRules > 0 && (
            <Button
              className={activeRules > 0 ? 'rounded-none rounded-r-md' : 'rounded'}
              variant="ghost"
              size="sm"
              color={'grass'}
              onClick={(e) => {
                e.stopPropagation()
                clearAllFilters()
              }}
            >
              <Icon icon={X} />
            </Button>
          )}
        </div>
      }
      align="start"
      className="w-fit max-w-[700px] p-0"
    >
      <div>
        <FilterQueryBuilder
          schema={schema}
          initialQueryConfig={initialQueryConfig}
          onCancel={() => setIsOpened(false)}
          onSave={(newQueryConfig) => {
            setActiveRules(newQueryConfig.rulesCount)
            const newTableFilter = (newQueryConfig.query ? JSON.parse(newQueryConfig.query) : undefined) as
              | QBSchema
              | undefined
            onTableFilterUpdated(newTableFilter)
            setIsOpened(false)
          }}
        />
      </div>
    </Popover>
  )
}

// eslint-disable-next-line react/display-name
const MDataGrid = memo(
  <R, SR = unknown, K extends React.Key = React.Key>(props: ComponentProps<typeof DataGrid<R, SR, K>>) => {
    return <DataGrid {...props} />
  },
  (prevProps, nextProps) => {
    const { columns: prevColumns, rows: prevRows, sortColumns: prevSortColumns } = prevProps
    const { columns: nextColumns, rows: nextRows, sortColumns: nextSortColumns } = nextProps

    if (prevSortColumns?.length !== nextSortColumns?.length) {
      return false
    }

    if (JSON.stringify(prevSortColumns) !== JSON.stringify(nextSortColumns)) {
      return false
    }

    if (prevColumns.length !== nextColumns.length || prevRows.length !== nextRows.length) {
      return false
    }
    if (JSON.stringify(prevColumns) !== JSON.stringify(nextColumns)) {
      return false
    }
    if (prevRows.length !== nextRows.length) {
      return false
    }
    if (JSON.stringify(prevRows) !== JSON.stringify(nextRows)) {
      return false
    }

    return true
  }
)

type ParsedColumn = Column<unknown, unknown> & {
  key: string
  name: string
  width?: number
  cell: Omit<DefaultDataGridCells, 'value'>
  cellClass?: string | undefined
  headerCellClass?: string | undefined
  resizable?: boolean
  sortable?: boolean
  minWidth?: number
  schema?: Record<string, any>
}

function parseColumns(
  table: ClientOutputs['getTable']['table'],
  columnSizes: Record<string, number> = {}
): ParsedColumn[] {
  const defaultColumnWidth = 150
  const defaultColumns: ParsedColumn[] = [
    {
      key: 'id',
      name: '#',
      cell: { type: 'number' },
      resizable: true,
      width: columnSizes['id'] ?? DEFAULT_LIMIT,
      editable: false,
    },
    {
      key: 'createdAt',
      name: 'Created At',
      cell: { type: 'date' },
      resizable: true,
      width: columnSizes['createdAt'] ?? defaultColumnWidth,
      editable: false,
    },
    {
      key: 'updatedAt',
      name: 'Updated At',
      cell: { type: 'date' },
      resizable: true,
      width: columnSizes['updatedAt'] ?? defaultColumnWidth,
      editable: false,
    },
  ]

  const emptyCol: ParsedColumn[] = [
    {
      key: '$empty',
      name: '',
      cell: { type: 'empty' },
      resizable: true,
      sortable: false,
      minWidth: 0,
      editable: false,
    },
  ]

  const sortedColumns = Object.entries(table.schema.properties).sort(
    ([, col1], [, col2]) => col1['x-zui'].index - col2['x-zui'].index
  )

  const columns: ParsedColumn[] = sortedColumns.map(([key, colSchema]) => {
    const computed = colSchema['x-zui'].computed
    const cell = match<typeof colSchema, Omit<DefaultDataGridCells, 'value'>>(colSchema)
      .with({ pattern: '^file_[0-9A-Z]{26}$' }, () => ({ type: 'file' }))
      .with({ type: 'string', format: 'date-time' }, () => ({ type: 'date' }))
      .with({ type: 'object' }, () => ({ type: 'object' }))
      .with({ type: 'string', enum: P.array(P.string) }, () => ({
        type: 'enum',
        options: colSchema.enum?.map((option) => ({ value: option })),
      }))
      .with({ type: 'string' }, () => ({ type: 'string' }))
      .with({ type: 'number' }, () => ({ type: 'number' }))
      .with({ type: 'boolean' }, () => ({ type: 'boolean' }))
      .with({ type: 'array' }, () => ({ type: 'array', cells: [] }))
      .with({ type: 'null' }, () => ({ type: 'string' })) // TODO: This is a hack to get the null type to work
      .exhaustive()

    return {
      key,
      name: key,
      cell,
      enum: colSchema.enum,
      width: columnSizes[key] ?? defaultColumnWidth,
      cellClass: computed ? 'computed-cell' : undefined,
      headerCellClass: computed ? 'computed-cell' : undefined,
      resizable: true,
      editable: !computed,
      schema: colSchema,
    }
  })

  return [...defaultColumns, ...columns, ...emptyCol]
}

function parseRows({
  tableRows,
  parsedColumns,
  files = [],
}: {
  tableRows: Row[]
  parsedColumns: ParsedColumn[]
  files: File[]
  pageSize?: number
}): Record<string, DefaultDataGridCells>[] {
  const filesMap = files.reduce(
    (acc, file) => {
      acc[file.id] = file
      return acc
    },
    {} as Record<string, File>
  )

  const rows = tableRows.map(({ id, createdAt, updatedAt, ...data }) => {
    const row: Record<string, DefaultDataGridCells> = {}
    for (const [_, column] of parsedColumns.entries()) {
      const { key } = column

      row[key] = match<DefaultDataGridCells, DefaultDataGridCells>({
        ...column.cell,
        value: data[key],
      } as DefaultDataGridCells)
        .with(
          { type: 'file' },
          ({ value }) => {
            const { contentType, key, url, createdAt } = filesMap[value ?? ''] ?? {}
            const creationDate = createdAt ? new Date(createdAt) : undefined
            return {
              type: 'file',
              value,
              contentType,
              url,
              createdAt: creationDate,
              name: key,
            } // TODO: Will need to fetch all the files from the files API to get more info
          } // TODO: Will need to fetch all the files from the files API to get more info
        )
        .with({ type: 'array' }, ({ value }) => {
          return {
            type: 'object',
            value,
          }
        })
        .with({ type: 'enum', value: P.string, enum: P.array(P.string) }, ({ value, enum: enumValues }) => {
          return {
            type: 'enum',
            value,
            options: enumValues.map((option) => ({ value: option })),
          }
        })
        .with({ type: 'string', value: P.string.regex(/^https?:\/\//) }, ({ value }) => ({
          type: 'url',
          value,
        }))
        .with({ type: 'string', value: P.string.or(P.nullish) }, ({ value }) => ({
          type: 'string',
          value,
        }))
        .with({ type: 'number', value: P.number.or(P.nullish) }, ({ value }) => ({
          type: 'number',
          value,
        }))
        .with({ type: 'object', value: P.any }, ({ value }) => {
          return {
            type: 'object',
            value,
          }
        })
        .with({ type: 'date' }, ({ value }) => ({
          type: 'date',
          value: value ? new Date(value) : null,
        }))
        .with({ type: 'boolean' }, ({ value }) => ({ type: 'boolean', value }))
        .otherwise((cell) => ({ ...cell }))
    }

    row.createdAt = { type: 'date', value: createdAt ? new Date(createdAt) : null }
    row.updatedAt = { type: 'date', value: updatedAt ? new Date(updatedAt) : null }
    row.id = { type: 'number', value: id }

    return row
  })
  return [...rows, ...generateRows({ columns: parsedColumns, type: 'empty', count: Math.max(24 - rows.length, 0) })]
}

function serializeRows(
  rows: Record<string, DefaultDataGridCells>[],
  changes?: number[]
): ClientInputs['upsertTableRows']['rows'] {
  return rows
    .filter((_, index) => (changes?.length ? changes.includes(index) : true))
    .map((row) => {
      const output: Record<string, string | number | boolean | null | object> = {}
      Object.entries(row).forEach(([key, value]) => {
        switch (value.type) {
          case 'array':
          case 'skeleton':
          case 'empty':
            break
          default:
            if (key === 'createdAt' || key === 'updatedAt') {
              break
            }
            output[key] = value.value
        }
      })
      if (Object.keys(output).length === 0) {
        return null
      }
      return output
    })
    .filter(Boolean) as ClientInputs['upsertTableRows']['rows']
}

function generateRows({
  count = DEFAULT_LIMIT,
  type = 'skeleton',
  columns,
}: {
  columns: ParsedColumn[]
  type?: 'skeleton' | 'empty'
  count?: number
}) {
  const skeletonRow = columns.reduce(
    (acc, { key }) => {
      acc[key] = { type }
      return acc
    },
    {} as Record<string, DefaultDataGridCells>
  )

  return Array.from({ length: count }).map(() => skeletonRow)
}
